import { slugify } from '@/shared/lib/slugify';

/**
 * Ключ поля из его названия.
 *
 * Ключ видят разработчики сайта: `record.data.title`, а не
 * `record.data.zagolovok`. Поэтому частые названия полей переводятся по
 * словарю основ («Заголовок» → `title`, «Дата начала» → `date_start`), а
 * транслитом становятся только слова, которых в словаре нет. Основа, а не
 * слово целиком: «цена», «цены», «стоимость» — одно и то же поле.
 *
 * Ключ уходит в JSON записи как имя свойства, поэтому в нём подчёркивания
 * вместо дефисов и он начинается с буквы. Пустая строка — законный ответ:
 * из названия ключ не выводится, и человек впишет его сам.
 */
export function keyify(name: string): string {
  const words = name
    .toLowerCase()
    .replace(/ё/g, 'е')
    .split(/[^a-zа-я0-9]+/)
    .filter((word) => word !== '' && !STOP_WORDS.has(word));

  const parts = words.map((word) => translate(word) ?? slugify(word).replace(/-/g, '_'));

  return parts
    .join('_')
    .replace(/_+/g, '_')
    .replace(/^[^a-z]+/, '')
    .slice(0, 60)
    .replace(/_+$/, '');
}

function translate(word: string): string | undefined {
  let best: [string, string] | undefined;

  for (const entry of STEMS) {
    if (word.startsWith(entry[0]) && (best === undefined || entry[0].length > best[0].length)) {
      best = entry;
    }
  }

  return best?.[1];
}

const STOP_WORDS = new Set([
  // Притяжательные: «Наши проекты» — это `projects`, а не `nashi-projects`.
  'наш',
  'наши',
  'наша',
  'наше',
  'мой',
  'мои',
  'все',
  'и',
  'или',
  'для',
  'в',
  'во',
  'на',
  'с',
  'со',
  'по',
  'о',
  'об',
  'к',
  'из',
  'от',
]);

// Основа → английское слово. Порядок не важен: выигрывает самая длинная
// подходящая основа («подзаголов» раньше «заголов»).
const STEMS: [string, string][] = [
  ['заголов', 'title'],
  ['подзаголов', 'subtitle'],
  ['назван', 'title'],
  ['описан', 'description'],
  ['текст', 'text'],
  ['содерж', 'content'],
  ['контент', 'content'],
  ['кратк', 'short'],
  ['анонс', 'excerpt'],
  ['цен', 'price'],
  ['стоимост', 'price'],
  ['скидк', 'discount'],
  ['валют', 'currency'],
  ['дат', 'date'],
  ['врем', 'time'],
  ['начал', 'start'],
  ['оконч', 'end'],
  ['конц', 'end'],
  ['картин', 'image'],
  ['изображ', 'image'],
  ['фото', 'photo'],
  ['обложк', 'cover'],
  ['галере', 'gallery'],
  ['видео', 'video'],
  ['файл', 'file'],
  ['документ', 'document'],
  ['иконк', 'icon'],
  ['логотип', 'logo'],
  ['фон', 'background'],
  ['ссылк', 'link'],
  ['сайт', 'website'],
  ['адрес', 'address'],
  ['город', 'city'],
  ['стран', 'country'],
  ['площадк', 'venue'],
  ['телефон', 'phone'],
  ['почт', 'email'],
  ['имя', 'name'],
  ['имен', 'name'],
  ['фамил', 'last_name'],
  ['автор', 'author'],
  ['компан', 'company'],
  ['должност', 'position'],
  ['биограф', 'bio'],
  ['категор', 'category'],
  ['рубрик', 'category'],
  ['тег', 'tags'],
  ['метк', 'tags'],
  ['статус', 'status'],
  ['порядок', 'order'],
  ['сортир', 'sort'],
  ['вопрос', 'question'],
  ['ответ', 'answer'],
  ['пункт', 'items'],
  ['меню', 'menu'],
  ['кнопк', 'button'],
  ['цвет', 'color'],
  ['размер', 'size'],
  ['вес', 'weight'],
  ['количеств', 'quantity'],
  ['артикул', 'sku'],
  ['товар', 'product'],
  ['услуг', 'service'],
  ['формат', 'format'],
  ['характерист', 'specs'],
  ['свойств', 'property'],
  ['значен', 'value'],
  ['ключев', 'keywords'],
  ['рейтинг', 'rating'],
  ['отзыв', 'review'],
  ['мероприят', 'event'],
  ['событ', 'event'],
  ['главн', 'main'],
  ['показ', 'show'],
  ['скрыт', 'hidden'],
  ['актив', 'active'],
  ['опублик', 'published'],
  ['соцсет', 'social'],
  ['тип', 'type'],
  ['год', 'year'],
  ['номер', 'number'],
  ['код', 'code'],
  ['полн', 'full'],
  ['нов', 'new'],
  // Частые названия коллекций — для короткого имени типа.
  ['новост', 'news'],
  ['стать', 'articles'],
  ['блог', 'blog'],
  ['страниц', 'pages'],
  ['проект', 'projects'],
  ['сотрудник', 'team'],
  ['команд', 'team'],
  ['ваканс', 'jobs'],
  ['кейс', 'cases'],
  ['партнер', 'partners'],
  ['клиент', 'clients'],
  ['настройк', 'settings'],
  ['шапк', 'header'],
  ['подвал', 'footer'],
  ['заказ', 'orders'],
  ['заявк', 'requests'],
];

/**
 * Короткое имя типа из названия — тем же словарём, что ключ поля: «Новости»
 * → `news`, а не `novosti`. Через дефис: оно уходит в адреса API.
 */
export function slugOf(name: string): string {
  return keyify(name).replace(/_/g, '-');
}
